import React, { use, useEffect } from 'react';
import DashboardChart from '../../components/chart-panel';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { useTranslation } from '@hooks/index';
import { useGetStudyDashboardPanelsData } from '@apis/bff-api/central-monitoring';

const ChartView = () => {
    const { t }: { t: (key: string) => string } = useTranslation('cm');
    const { current } = use(StudySiteHubContext);
    const { legendToggleValue } = usePageToolbar();
    const {
        sessionId: siteFilterSessionId,
        filtersChanged$,
        isRecalculating,
        isFiltered,
    } = useStudyDataFilterContext();
    const { data, isMutating, error, trigger } = useGetStudyDashboardPanelsData(
        current.studyRef,
        current.dataset.id,
        current.datasetFilters.dataSetsCount ?? 0,
        current.datasetFilters.dataSetFilter ?? 0,
        siteFilterSessionId,
    );

    useEffect(() => {
        const triggerRequest = () => {
            if (!siteFilterSessionId) {
                return;
            }
            trigger().catch((err) => window.console.log(err));
        };
        !(isRecalculating && isFiltered) && triggerRequest();

        // Subscribe to the filter changed event emitted by the Filter UI, and re-trigger the get
        const subscription = filtersChanged$.subscribe(() => {
            triggerRequest();
        });

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [
        current.studyRef,
        current.dataset.id,
        current.datasetFilters.dataSetsCount,
        current.datasetFilters.dataSetFilter,
        siteFilterSessionId,
        isRecalculating,
    ]);

    let content;

    if (error || isMutating) {
        if (error) {
            content = <ErrorMessage error={t('studyHubPage.charts.errorLoading')} />;
        } else {
            content = <Loading />;
        }
        return (
            <Card title={t('studyHubPage.charts.panels')} data-testid="chart-view">
                {content}
            </Card>
        );
    }

    if (data?.length === 0) {
        return <InfoField message={t('studyHubPage.charts.zeroPanelsConfigured')} type="Info" layout="center" />;
    }

    return (
        <div className="chart-view">
            {(data ?? []).map((panel) => (
                <DashboardChart
                    key={panel.reference}
                    panel={panel}
                    showLegend={legendToggleValue?.current}
                    hidePanelSetup={current.hidePanelSetup}
                />
            ))}
        </div>
    );
};

export default ChartView;
